import { dehydrate, QueryClient } from "react-query"
import type { NextPage } from "next"
import { GetServerSidePropsContext, GetServerSidePropsResult } from "next"
import { useRouter } from "next/router"
import { fetchProductById, fetchProductsList } from "@/api/productsAPI"
import { BreadcrumbItemType } from "@/components/Breadcrumbs/types"
import {
  ProductPage,
  SSRPropsReturnType,
} from "@/components/Products/ProductPage"
import { getProductPath } from "@/hooks/product/helpers"
import {
  setCurrentCategory,
  updateBreadcrumbs,
} from "@/store/reducers/catalogSlice"
import { wrapper } from "@/store/store"

const ProductDetailPage: NextPage<SSRPropsReturnType> = (rest) => {
  const router = useRouter()

  return <ProductPage key={router.pathname} {...rest} />
}

export const getServerSideProps = wrapper.getServerSideProps(
  (reduxStore) =>
    async (
      ctx: GetServerSidePropsContext,
    ): Promise<GetServerSidePropsResult<SSRPropsReturnType>> => {
      const { params, res, req } = ctx

      const productQuery = (params?.product as string) || ""

      if (!productQuery) {
        return {
          notFound: true,
        }
      }

      const queryClient = new QueryClient()
      const initialProduct = await queryClient.fetchQuery({
        queryKey: ["product", productQuery],
        queryFn: () =>
          fetchProductById({
            server: true,
            uuid: productQuery,
            req: req,
            res: res,
          }).catch((err) => console.log("err [initialProduct] ", err)),
      })

      if (!initialProduct) {
        return {
          notFound: true,
        }
      }

      // у товара могут быть несколько категорий
      // хлебные крошки должны строиться в соответствии с категорией из которой перешли на товар

      // сценарий 1
      // если на товар перешли напрямую по ссылке,
      // берем первую категорию в массиве
      // и от нее строим хлебные крошки

      // сценарий 2
      // если перешли из каталога
      // проверяем относится ли товар к этой категории
      // (так, если перешли из поиска или других секций на странице каталога
      // товар может не относиться к текущей странице категории)

      // TODO: временно отключено из-за надобности вывести хл крошки для СЕО а sessionStorage не доступен на этом этапе
      // const uuidSavedCategory = getLastCategoryStorage()
      // if (!!uuidSavedCategory && categoriesProduct.includes(uuidSavedCategory)) {

      const {
        categories,
        uuid = "",
        name,
        alias,
        companions,
        analogs,
      } = initialProduct

      const promises = []

      const companionsProduct = (companions || [])
        .map((item) => item.uuid)
        .join(",")
      const analogsProduct = [
        ...(analogs?.fast || []),
        ...(analogs?.other || []),
      ]
        .map((item) => item.uuid)
        .join(",")

      if (companionsProduct.length > 0) {
        promises.push(
          queryClient.prefetchQuery({
            queryKey: ["companions", companionsProduct],
            queryFn: () =>
              fetchProductsList({
                uuids: companionsProduct,
                server: true,
              }),
          }),
        )
      }

      if (analogsProduct.length > 0) {
        promises.push(
          queryClient.prefetchQuery({
            queryKey: ["analogs", analogsProduct],
            queryFn: () =>
              fetchProductsList({
                uuids: analogsProduct,
                server: true,
              }),
          }),
        )
      }

      try {
        await Promise.all(promises)
      } catch (error) {
        console.error("Error prefetching products:", error)
      }

      const categoriesProduct = categories ?? []
      const categoriesStore = reduxStore.getState().categories.categories
      const categoriesFetched = categoriesStore?.fetched || {}

      const categoryProduct = categoriesFetched[categoriesProduct[0] ?? ""]

      if (!!categoryProduct) {
        const breadcrumbProduct: BreadcrumbItemType = {
          path: getProductPath(alias),
          title: String(name),
        }

        reduxStore.dispatch(setCurrentCategory({ ...categoryProduct }))
        reduxStore.dispatch(
          updateBreadcrumbs({
            category: categoryProduct,
            categories: categoriesStore,
            append: {
              [uuid]: breadcrumbProduct,
            },
          }),
        )
      }

      return {
        props: {
          product: initialProduct,
          alias: productQuery,
          dehydratedState: dehydrate(queryClient),
        },
      }
    },
)

export default ProductDetailPage
